import { useParams, Navigate, useNavigate } from 'react-router-dom'
import { useLesson } from '../../hooks/useApi'
import { useTelegramBackButton } from '../../hooks/useTelegramBackButton'
import { ContentRenderer, ImageSlider } from '../CardPage/components'
import type { ContentBlock } from '../../types/api'
import { File, Timer, ClipboardCheck, Calculator } from 'lucide-react'
import { CALCULATOR_RECIPE_IDS } from '../../constants/calculatorAccess'
import LoadingScreen from '../../components/LoadingScreen/LoadingScreen'
import './LessonPage.css'

const LessonPage = () => {
  const { courseId, lessonId } = useParams<{ courseId: string; lessonId: string }>()
  const navigate = useNavigate()
  const lessonIdNumber = lessonId ? parseInt(lessonId, 10) : null
  const { data, isLoading, error } = useLesson(lessonIdNumber)

  useTelegramBackButton(true)

  if (!lessonId || isNaN(lessonIdNumber!)) {
    return <Navigate to={courseId ? `/courses/${courseId}` : '/courses'} replace />
  }

  if (isLoading) {
    return <LoadingScreen isLoading={true} />
  }

  if (error) {
    const errorDetail = (error as any)?.response?.data?.detail

    return (
      <div className="lesson-page-error">
        <h2>Ошибка загрузки</h2>
        <p>{errorDetail || 'Не удалось загрузить урок'}</p>
        <button onClick={() => window.history.back()}>Вернуться назад</button>
      </div>
    )
  }

  if (!data?.lesson) {
    return (
      <div className="lesson-page-error">
        <h2>Урок не найден</h2>
        <button onClick={() => window.history.back()}>Вернуться назад</button>
      </div>
    )
  }

  const { lesson } = data
  const content = lesson.content
  const info = lesson.info

  return (
    <div
      className={`lesson-page ${
        content.slider_images &&
        content.slider_images.length === 0 &&
        'safe-area-inset'
      }`}
    >
      {content.slider_images && content.slider_images.length > 0 && (
        <ImageSlider images={content.slider_images} title={info.title} />
      )}

      <div className="lesson-page-content">
        <div className="card-info">
          <div className="card-header-row">
            <h1 className="card-title">{info.title}</h1>
          </div>

          {content.card_type === 'recipe' && (
            <div className="recipe-meta">
              {content.cooking_time && (
                <div className="cooking-time">
                  <Timer size={24} className="timer-icon" />
                  <p>{content.cooking_time}</p>
                </div>
              )}

              <div className="recipe-actions">
                {content.file_url && (
                  <button
                    onClick={() =>
                      window.open(
                        content.file_url!,
                        '_blank',
                        'noopener,noreferrer'
                      )
                    }
                    className="download-btn"
                  >
                    <File size={16} />
                    <p>Скачать рецепт</p>
                  </button>
                )}

                {lesson.base.test_url && (
                  <button
                    className="lesson-test-btn"
                    onClick={() => {
                      const tg = window.Telegram?.WebApp
                      if (tg) {
                        tg.openLink(lesson.base.test_url!)
                      } else {
                        window.open(lesson.base.test_url!, '_blank', 'noopener,noreferrer')
                      }
                    }}
                  >
                    <ClipboardCheck size={16} />
                    <p>Пройти тест</p>
                  </button>
                )}

              </div>

              {CALCULATOR_RECIPE_IDS.has(lesson.base.card_id) && (
                <button
                  className="calculator-btn"
                  onClick={() => navigate(`/calculator/${lesson.base.card_id}?lessonId=${lesson.base.lesson_id}`)}
                >
                  <Calculator size={16} />
                  <p>Пересчитать</p>
                </button>
              )}
            </div>
          )}

          {content.card_type !== 'recipe' && lesson.base.test_url && (
            <div className="recipe-actions" style={{ marginTop: 12 }}>
              <button
                className="lesson-test-btn"
                onClick={() => {
                  const tg = window.Telegram?.WebApp
                  if (tg) {
                    tg.openLink(lesson.base.test_url!)
                  } else {
                    window.open(lesson.base.test_url!, '_blank', 'noopener,noreferrer')
                  }
                }}
              >
                <ClipboardCheck size={16} />
                <p>Пройти тест</p>
              </button>
            </div>
          )}
        </div>

        {content.content && content.content.length > 0 && (
          <ContentRenderer content={content.content as ContentBlock[]} />
        )}
      </div>
    </div>
  )
}

export default LessonPage
